export const INSTRUCTOR_ONBOARDING_REQUIRED_FIELDS = [
  'FirstName',
  'LastName',
  'Phone',
  'Address',
  'StreetAddress',
  'City',
  'State',
  'Zip',
  'location',
  'distanceValue',
  'ClassTypes',
  'instructorAgreementDate',
];

const clean = (value) => String(value || '').trim();

const hasLocation = (location) => (
  location
  && Number.isFinite(Number(location.latitude))
  && Number.isFinite(Number(location.longitude))
);

export const normalizeOnboardingPhoneDigits = (value) => {
  const digits = clean(value).replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

export const isValidOnboardingPhoneNumber = (value) => {
  const digits = normalizeOnboardingPhoneDigits(value);
  return digits.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
};

const hasClassTypes = (classTypes, customClassTypes) => (
  (Array.isArray(classTypes)
    ? classTypes.filter(Boolean).length > 0
    : Boolean(String(classTypes || '').trim()))
  || (Array.isArray(customClassTypes)
    ? customClassTypes.some((item) => Boolean(clean(item?.value || item)))
    : Boolean(String(customClassTypes || '').trim()))
);

const hasAcceptedAgreement = (instructor = {}) => Boolean(
  instructor?.instructorAgreementDate || instructor?.InstructorAgreementAcceptedAt
);

export const getMissingInstructorOnboardingFields = (instructor = {}) => {
  if (instructor?.InstructorOnboardingCompletedAt && hasAcceptedAgreement(instructor)) {
    return [];
  }

  return INSTRUCTOR_ONBOARDING_REQUIRED_FIELDS.filter((field) => {
    const value = instructor?.[field];
    if (field === 'location') {
      return !hasLocation(value);
    }
    if (field === 'ClassTypes') {
      return !hasClassTypes(value, instructor?.CustomClassTypes);
    }
    if (field === 'Phone') {
      return !isValidOnboardingPhoneNumber(value);
    }
    if (field === 'instructorAgreementDate') {
      return !hasAcceptedAgreement(instructor);
    }
    return value === undefined || value === null || value === '';
  });
};

export const isInstructorOnboardingComplete = (instructor = {}) => (
  getMissingInstructorOnboardingFields(instructor).length === 0
);

export const normalizeOnboardingClassTypes = (classTypes) => {
  if (Array.isArray(classTypes)) return classTypes.map(clean).filter(Boolean);
  if (!classTypes) return [];

  return String(classTypes)
    .split(',')
    .map(clean)
    .filter(Boolean);
};

export const normalizeUserEnteredClassTypes = (customClassTypes, nowMs = Date.now()) => {
  const values = Array.isArray(customClassTypes)
    ? customClassTypes
    : String(customClassTypes || '').split(',');
  const seen = new Set();

  return values.reduce((items, item) => {
    const value = clean(item?.value || item).replace(/\s+/g, ' ');
    const key = value.toLowerCase();

    if (!value || seen.has(key)) return items;

    seen.add(key);
    items.push({
      value,
      source: 'user_entered',
      createdAt: Number(item?.createdAt) || nowMs,
    });

    return items;
  }, []);
};

export const buildInstructorOnboardingUpdate = ({
  formData = {},
  stripeSkipped = false,
  nowMs = Date.now(),
} = {}) => {
  const address = clean(formData.Address);
  const payload = {
    FirstName: clean(formData.FirstName),
    LastName: clean(formData.LastName),
    Phone: clean(formData.Phone),
    Address: address,
    StreetAddress: address,
    City: clean(formData.City),
    State: clean(formData.State),
    Zip: clean(formData.Zip),
    location: formData.location || null,
    distanceValue: clean(formData.distanceValue),
    ClassTypes: normalizeOnboardingClassTypes(formData.ClassTypes),
    CustomClassTypes: normalizeUserEnteredClassTypes(formData.CustomClassTypes, nowMs),
    InstructorOnboardingCompletedAt: nowMs,
  };

  if (formData.agreementAccepted) {
    payload.instructorAgreementDate = nowMs;
    payload.InstructorAgreementAcceptedAt = nowMs;
    payload.InstructorAgreementURL = clean(formData.agreementUrl);
  }

  if (stripeSkipped) {
    payload.InstructorOnboardingStripeSkippedAt = nowMs;
  }

  return payload;
};

export const buildInstructorOnboardingCompletedActivity = ({
  email,
  userDocId,
  stripeSkipped = false,
  nowMs = Date.now(),
} = {}) => {
  const normalizedEmail = clean(email).toLowerCase();

  return {
    type: 'instructor_onboarding_completed',
    title: 'Instructor Onboarding Completed',
    body: `${normalizedEmail || 'Instructor'} completed guided onboarding.`,
    timestamp: nowMs,
    actor: {
      userDocId,
      email: normalizedEmail,
    },
    metadata: {
      stripeSkipped,
    },
  };
};

export const buildInstructorOnboardingStripeSkippedActivity = ({
  email,
  userDocId,
  nowMs = Date.now(),
} = {}) => {
  const normalizedEmail = clean(email).toLowerCase();

  return {
    type: 'instructor_onboarding_stripe_skipped',
    title: 'Stripe Setup Skipped',
    body: `${normalizedEmail || 'Instructor'} skipped Stripe setup during onboarding.`,
    timestamp: nowMs,
    actor: {
      userDocId,
      email: normalizedEmail,
    },
  };
};
